import { useEffect, useState } from 'react'
import { useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Box, Link, Typography } from '@mui/material'
import request from 'api'
import CookieAgreementCountry from 'components/footer/cookie-agreement/assets/json/cookie-agreement-country.json'
import { Loading } from 'components/ui'
import { usePageTitle } from 'hooks'
import { countryCode, languageCode } from 'utils/i18n'

import ConsentDetail from './consent-detail'
import ConsentList from './consent-list'

type Consent = {
  root: boolean
  id: string
  title: string
  uri: string
}

type LinkItem = {
  title: string
  url: string
}

export default function Policy() {
  const { consentType, childId } = useParams()
  const navigate = useNavigate()
  const location = useLocation()
  const [searchParams] = useSearchParams()
  const paramCountryCode = searchParams.get('countryCode') || undefined
  const paramLocale = searchParams.get('locale') || undefined

  const [isLoading, setIsLoading] = useState<boolean>(true)
  const [consentList, setConsentList] = useState<Consent[]>([])
  const [title, setTitle] = useState<string>('')
  const [link, setLink] = useState<LinkItem | null>(null)

  const getConsentApi = (type: string, includeChild: boolean) => {
    const appKey = process.env.REACT_APP_CONSENT_APP_KEY
    const now = (new Date().getTime() / 1000).toFixed(0)

    return request
      .get('/v1/consent', {
        headers: {
          'x-app-id': appKey,
          'x-os-version': 'WEB',
          'x-package-name': 'myAccount3',
          'x-package-version': '1.0.0',
          'x-device-id': 'myAccount3',
          'x-model-name': navigator.userAgent,
          'x-requested': now,
          'x-started': now
        },
        params: {
          applicationRegion: countryCode,
          region: countryCode,
          language: languageCode,
          appKey,
          type,
          includeChild
        }
      })
      .then((res) => {
        if (res.status !== 200) {
          throw new Error('Failed getConsentApi')
        } else {
          const data = res.data as Consent[]
          setConsentList(data)

          if (includeChild === false) {
            setTitle(data[0]?.title)
          }
        }
      })
      .catch(() => {
        setConsentList([])
      })
      .finally(() => {
        setIsLoading(false)
      })
  }

  const handleCookieLogic = () => {
    const isEuropeCountry =
      CookieAgreementCountry.europe.find((country) => country === countryCode?.toString()?.toUpperCase()) !== undefined
    const isLatinAmericaCountry =
      CookieAgreementCountry.latinAmerica.find((country) => country === countryCode?.toString()?.toUpperCase()) !== undefined

    if (isEuropeCountry || isLatinAmericaCountry) {
      setLink(null)
      getConsentApi('CK', false)
    } else {
      navigate('/dashboard/intro', { replace: true })
    }
  }

  const getConsent = () => {
    setIsLoading(true)

    if (childId !== undefined) {
      const consent = consentList.filter((item) => item.id.toString() === childId.toString())
      if (consent.length !== 0) {
        setConsentList(consent as Consent[])
        setTitle(consent[0]?.title)
        setIsLoading(false)
      } else {
        const lastIndex = location.pathname.lastIndexOf('/')
        navigate(location.pathname.slice(0, lastIndex))
      }
    } else {
      switch (consentType) {
        case 'terms':
          navigate('/policies/terms-conditions/latest')
          break
        case 'service':
          navigate('/policies/terms-conditions/services')
          break
        case 'privacy':
          navigate('/policies/privacy-notices/latest')
          break
        case 'operator':
          navigate('/policies/privacy-notices/operators')
          break
        case 'cookie':
          handleCookieLogic()
          break
        case 'financial':
          setLink(null)
          getConsentApi('NOFI', false)
          break
        default:
          navigate(`/error/404?url=${location.pathname}`, { replace: true })
      }
    }
  }

  usePageTitle(title)

  useEffect(() => {
    getConsent()
  }, [location])

  return (
    <Box sx={{ width: '100%', height: '100%', padding: { xs: '24px', md: '0' } }}>
      {isLoading ? (
        <Loading fullScreen={true} />
      ) : (
        <Box sx={{ display: 'flex', justifyContent: 'center' }}>
          <Box sx={{ width: { xs: '100%', md: '1040px' } }}>
            <Box textAlign='center' paddingTop='36px'>
              <Typography variant='h1' fontWeight='500' fontSize={{ xs: '36px', md: '48px' }}>
                {title}
              </Typography>
            </Box>
            {consentList.length <= 1 ? (
              <ConsentDetail
                title={consentList[0]?.title || ''}
                uri={consentList[0]?.uri || ''}
                paramCountryCode={paramCountryCode}
                paramLocale={paramLocale}
              />
            ) : (
              <ConsentList list={consentList} />
            )}
            {link !== null && (
              <Box sx={{ marginTop: { xs: '52px', md: '72px' }, display: 'flex', justifyContent: 'center' }}>
                <Link
                  component='button'
                  type='button'
                  color='text.secondary'
                  sx={{
                    fontSize: '16px',
                    fontHeight: '24px',
                    fontWeight: '700',
                    minWidth: '312px'
                  }}
                  onClick={() => navigate(link.url)}
                  data-testid='Link15F5E462'
                >
                  {link.title}
                </Link>
              </Box>
            )}
          </Box>
        </Box>
      )}
    </Box>
  )
}
